<!-- Full-screen board beside the app's menu, laid out like the CGL scheduler: one toolbar, then jobs + board. -->
<div class="schedulerPage">
  <div class='schedBar'>
    <h2 class='schedHeading'>Workshop Scheduler</h2>
    <button class='barBtn' (click)='this.showJobs.set(!this.showJobs())' [class.on]='this.showJobs()'>
      <i class='fas fa-list'></i> {{ this.showJobs() ? 'Hide jobs' : 'Show jobs' }}
    </button>

    <div class='schedNav'>
      <button class='barBtn navBtn' (click)='this.step(-1)' title='Previous' aria-label='Previous'><i class='fas fa-chevron-left'></i></button>
      <b class='schedTitle'>{{ this.title() }}</b>
      <button class='barBtn navBtn' (click)='this.step(1)' title='Next' aria-label='Next'><i class='fas fa-chevron-right'></i></button>
    </div>

    <button class='barBtn' (click)='this.step(0)'>Today</button>
    <input type='date' class='barDate' [value]='this.anchor()' (change)='this.onDateInput($any($event.target).value)' aria-label='Date'>
    <div class='seg' role='group' aria-label='View'>
      @for (v of this.views; track v.key) {
        <button class='segBtn' [class.on]='this.viewKey() === v.key' (click)='this.setView(v.key)'>{{ v.label }}</button>
      }
    </div>

    <div class='zoomCtl' role='group' aria-label='Zoom'>
      <button class='zoomBtn' (click)='this.zoomOut()' [disabled]='!this.canZoomOut()' title='Zoom out (Ctrl + scroll)' aria-label='Zoom out'>−</button>
      <button class='zoomNow' (click)='this.zoomReset()' title='Reset zoom'>{{ this.zoom() * 100 | number:'1.0-0' }}%</button>
      <button class='zoomBtn' (click)='this.zoomIn()' [disabled]='!this.canZoomIn()' title='Zoom in (Ctrl + scroll)' aria-label='Zoom in'>+</button>
    </div>

    <button class='barBtn' (click)='this.undo()' [disabled]='!this.canUndo()' title='Undo (Ctrl+Z)'><i class="fas fa-undo"></i> Undo</button>
    <button class='barBtn' (click)='this.redo()' [disabled]='!this.canRedo()' title='Redo (Ctrl+Y)'><i class="fas fa-redo"></i> Redo</button>
    <button class='barBtn' routerLink='/workshop/scheduler/hours' title='Working hours for the workshop and each fitter'><i class="fas fa-user-clock"></i> Hours</button>
    <button class='barBtn' routerLink='/workshop/inspections/calendar' title='Inspection &amp; MOT Calendar'><i class="fas fa-calendar-check"></i> Calendar</button>
  </div>

  @if (this.error) { <div class='alert alert-danger schedError'>{{ this.error }}</div> }

  <div class='schedulerLayout'>
    <!-- To schedule: jobs are dragged from here with pointer events (the board takes over the drag). -->
    @if (this.showJobs()) {
    <aside class='queue'>
      <h3 class='panelTitle'>To schedule</h3>
      <p class='panelHint'>Drag a job onto a fitter. Drag blocks to move them, their edges to change the time; click a block for options.</p>
      <input type='search' class='form-control' placeholder='Search reg, job or inspection' [(ngModel)]='this.search' (input)='this.onSearch()'>
      <div class='queueList'>
        @for (job of this.jobs(); track job.jobType + job.id) {
          <div class='queueJob' (pointerdown)='this.startJobDrag($event, job)' [style.border-left-color]='this.isDueOverdue(job) ? "#b3261e" : job.colour'>
            <div class='queueReg'>{{ job.registration }}</div>
            <div class='queueTitle'>{{ job.title }}</div>
            <div class='queueMeta'>
              {{ job.jobType === 'inspection' ? 'Inspection' : 'Defect' }} · {{ this.formatMinutes(job.minutes) }}
              @if (job.dueDate) { · <span [class.overdue]='this.isDueOverdue(job)'>due {{ job.dueDate.split('-').reverse().join('/') }}</span> }
            </div>
          </div>
        } @empty {
          <p class='text-muted'>Nothing waiting to be scheduled.</p>
        }
      </div>
      <h3 class='panelTitle keyTitle'>Key</h3>
      <ul class='colourKey'>
        <li><span class='swatch' style='background:#006498'></span> Defect job</li>
        <li><span class='swatch' style='background:#0275d8'></span> Inspection (its type colour)</li>
        <li><span class='swatch' style='background:#2e7d32'></span> Done ✓</li>
        <li><span class='swatch overText'>Aa</span> Over its planned time</li>
        <li><span class='swatch offSwatch'></span> Not working (outside their hours)</li>
        <li><span class='swatch hatched'></span> Fitter absent / day off</li>
        <li><span class='swatch outsideSwatch'></span> Booked outside their hours</li>
        <li><span class='swatch' style='background:#b3261e'></span> Inspection overdue</li>
      </ul>
    </aside>
    }

    <!-- One scroll container: the time header sticks to the top, fitter labels to the left. -->
    <section class='board' #board (wheel)='this.onWheel($event)'
      [style.--label-w.px]='this.labelPx' [style.--day-w.px]='this.dayWidth()' [style.--hour-w.px]='this.pxPerHour()'>
      <div class='boardHead' [style.width.px]='this.labelPx + this.trackWidth()'>
        <div class='corner'>Fitter</div>
        <div class='headTrack' [style.width.px]='this.trackWidth()'>
          @for (d of this.days(); track d; let i = $index) {
            <div class='dayHead' [style.left.px]='i * this.dayWidth()' [style.width.px]='this.dayWidth()'>
              <div class='dayName'>{{ this.dayHeading(d) }}</div>
              @for (t of this.ticks(); track t.hour) {
                <span class='tick' [style.left.px]='t.x'>{{ (t.hour < 10 ? '0' : '') + t.hour }}:00</span>
              }
            </div>
          }
          @if (this.nowX() !== null) {
            <span class='nowFlag' [style.left.px]='this.nowX()'>{{ this.nowLabel() }}</span>
          }
        </div>
      </div>

      @if (!this.loading && this.fitters().length === 0) {
        <div class='alert alert-info emptyBoard'>There are no active fitters yet. Add fitters under Workshop &rsaquo; Fitters.</div>
      }

      @for (f of this.fitters(); track f.id) {
        @let t = this.totals(f.id);
        <div class='lane' [style.width.px]='this.labelPx + this.trackWidth()'>
          <div class='laneLabel'>
            <div class='fitterName'>{{ f.name }} @if (!f.active) { <span class='inactive'>(inactive)</span> }</div>
            <div class='fitterTotals' [class.busy]='t.load !== null && t.load > 100'>
              {{ this.formatMinutes(t.planned) }} planned · {{ t.actual }}h actual · {{ t.load === null ? '–' : t.load + '%' }}
            </div>
          </div>
          <div class='track' [attr.data-fitter]='f.id' [style.width.px]='this.trackWidth()'>
            @for (o of this.offHoursFor(f.id); track o.date + o.startMin) {
              <div class='offHours' [class.closedDay]='o.closed' [style.left.px]='this.left(o)' [style.width.px]='this.width(o.endMin - o.startMin)'
                [title]='o.closed ? o.reason : "Not working"'>
                @if (o.closed) { <span>{{ o.reason }}</span> }
              </div>
            }
            @for (a of this.absenceDays(f.id); track a.date) {
              <div class='absence' [style.left.px]='this.left({ date: a.date, startMin: this.range().startMin })' [style.width.px]='this.dayWidth()' [title]='a.label'>
                <span>{{ a.label }}</span>
              </div>
            }
            @for (b of this.blocksFor(f.id); track b.id) {
              <div class='block' [class.done]='b.status === "done"' [class.isDragging]='this.drag()?.blockId === b.id'
                [class.menuOpen]='this.menu?.block?.id === b.id' [class.outside]='this.isOutside(b)'
                [style.left.px]='this.left(b)' [style.width.px]='this.width(b.durMin)'
                [style.background-color]='b.status === "done" ? null : b.colour'
                [title]='this.blockTip(b)'
                (pointerdown)='b.status === "done" ? this.openMenu(b, $event.clientX, $event.clientY) : this.startBlockDrag($event, b, "move")'>
                @if (b.status !== 'done') {
                  <span class='grip gripStart' (pointerdown)='this.startBlockDrag($event, b, "resize-start")'></span>
                }
                <span class='blockText' [class.over]='this.isOver(b)'>
                  @if (b.status === 'done') { ✓ }
                  <b>{{ b.registration }}</b> {{ b.title }} · {{ this.formatMinutes(b.durMin) }}
                  @if (b.status === 'done') { · {{ b.actualHours }}h }
                </span>
                @if (b.status !== 'done') {
                  <span class='grip gripEnd' (pointerdown)='this.startBlockDrag($event, b, "resize-end")'></span>
                }
              </div>
            }
            @if (this.hover(); as h) {
              @if (h.fitterId === f.id) {
                <div class='preview' [class.blocked]='!h.verdict.ok' [style.left.px]='this.left(h)' [style.width.px]='this.width(h.durMin)'></div>
              }
            }
            @if (this.nowX() !== null) {
              <div class='nowLine' [style.left.px]='this.nowX()'></div>
            }
          </div>
        </div>
      }
    </section>
  </div>

  <!-- Ghost card following the pointer while dragging. -->
  @if (this.drag(); as d) {
    @if (this.hover() || d.mode === 'new') {
      <div class='ghost' [class.blocked]='this.ghostVerdict() && !this.ghostVerdict().ok'
        [style.left.px]='this.pointer().x + 14' [style.top.px]='this.pointer().y + 14'>
        <div class='ghostLabel'>{{ d.label }}</div>
        @if (this.hover(); as h) {
          <div>{{ this.hhmm(h.startMin) }}–{{ this.hhmm(h.startMin + h.durMin) }} · {{ this.formatMinutes(h.durMin) }}</div>
          @if (!h.verdict.ok) { <div class='ghostReason'>{{ h.verdict.message }}</div> }
        } @else {
          <div>{{ this.formatMinutes(d.durMin) }} – drop on a fitter</div>
        }
      </div>
    }
  }

  <!-- Block menu (opened by a click on a block). -->
  @if (this.menu; as m) {
    <div class='menuBackdrop' (click)='this.closeMenu()'></div>
    <div class='blockMenu' [style.left.px]='m.x' [style.top.px]='m.y'>
      <div class='menuHead'>
        <b>{{ m.block.registration }}</b> – {{ m.block.title }}<br />
        <small>{{ m.block.date.split('-').reverse().join('/') }} {{ this.hhmm(m.block.startMin) }}–{{ this.hhmm(m.block.startMin + m.block.durMin) }}</small>
      </div>
      @switch (this.dialog) {
        @case ('complete') {
          <label>Actual hours</label>
          <input type='number' class='form-control' min='0' max='24' step='0.25' [(ngModel)]='this.actualHours'>
          <label>Notes</label>
          <textarea class='form-control' rows='2' [(ngModel)]='this.notes'></textarea>
          @if (m.block.jobType === 'inspection') { <small class='text-muted'>Also marks the inspection done on this day.</small> }
          <div class='menuButtons'>
            <button class='btn btn-secondary btn-sm' (click)='this.complete()' [disabled]='this.busy'><i class='fas fa-check'></i> Complete</button>
            <button class='btn btn-secondary btn-sm' (click)='this.dialog = null'>Back</button>
          </div>
        }
        @case ('remaining') {
          <label>Time left on this job</label>
          <div class='remainingInputs'>
            <select class='form-control' [(ngModel)]='this.remainingHours'>
              @for (h of [0,1,2,3,4,5,6,7,8,9,10,11]; track h) { <option [ngValue]='h'>{{ h }} h</option> }
            </select>
            <select class='form-control' [(ngModel)]='this.remainingMinutes'>
              @for (mi of [0,15,30,45]; track mi) { <option [ngValue]='mi'>{{ mi }} m</option> }
            </select>
          </div>
          <small class='text-muted'>The block becomes this long from its start – longer if the job is running over.</small>
          <div class='menuButtons'>
            <button class='btn btn-secondary btn-sm' (click)='this.saveRemaining()'><i class='fas fa-save'></i> Save</button>
            <button class='btn btn-secondary btn-sm' (click)='this.dialog = null'>Back</button>
          </div>
        }
        @case ('notes') {
          <label>Notes</label>
          <textarea class='form-control' rows='3' [(ngModel)]='this.notes'></textarea>
          <div class='menuButtons'>
            <button class='btn btn-secondary btn-sm' (click)='this.saveNotes()'><i class='fas fa-save'></i> Save</button>
            <button class='btn btn-secondary btn-sm' (click)='this.dialog = null'>Back</button>
          </div>
        }
        @default {
          @if (m.block.status === 'done') {
            <div class='menuDone'>Completed · {{ m.block.actualHours }}h actual</div>
          } @else {
            @if (!this.isFuture(m.block)) {
              <button class='menuItem' (click)='this.openDialog("complete")'><i class='fas fa-check fa-fw'></i> Mark complete…</button>
            }
            <button class='menuItem' (click)='this.openDialog("remaining")'><i class='fas fa-hourglass-half fa-fw'></i> Update hours left…</button>
          }
          <button class='menuItem' (click)='this.openDialog("notes")'><i class='fas fa-sticky-note fa-fw'></i> Notes…</button>
          <button class='menuItem danger' (click)='this.remove()'><i class='fas fa-trash fa-fw'></i> Remove from schedule</button>
        }
      }
    </div>
  }

  @if (this.toast) { <div class='toastMessage'>{{ this.toast }}</div> }
</div>
